import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip as ChakraTooltip,
  VStack,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  FaTicketAlt,
  FaCalendarCheck,
  FaExternalLinkAlt,
  FaDownload,
  FaQrcode,
  FaCopy,
  FaInfoCircle,
  FaDollarSign,
} from "react-icons/fa";
import QRCode from "react-qr-code";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { apiUrl } from "../config";
import { UserAuth } from "../contexts/AuthContext";

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatCsvDate = (iso) => {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatCsvTime = (iso) => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const csvEscape = (value) => {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const formatChartDay = (yyyyMmDd) => {
  // Render "MM/DD" so 30 ticks fit comfortably on the x-axis
  const [, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}`;
};

const localizedField = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.en || value.fr || "";
};

const formatStarRating = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(1)} ★`;
};

const formatPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${Math.round(Number(n) * 100)}%`;
};

// Ordered so the stacked bar reads "new/lapsed → recent regulars" left to
// right. New/lapsed buckets use green (darkest on `never`) to highlight reach
// of fresh customers; recent regulars use grey shades from light to dark.
const PRIOR_VISIT_BAR_ORDER = [
  "never",
  "over_year",
  "within_year",
  "within_3_months",
  "within_month",
  "within_week",
];

const PRIOR_VISIT_LABELS = {
  within_week: "Within the last week",
  within_month: "Within the last month",
  within_3_months: "Within the last 3 months",
  within_year: "Within the last year",
  over_year: "Over a year ago",
  never: "Never visited before",
};

const PRIOR_VISIT_COLORS = {
  never: "green.700",
  over_year: "green.500",
  within_year: "green.300",
  within_3_months: "gray.300",
  within_month: "gray.500",
  within_week: "gray.700",
};

// Compact labels for chart axes where horizontal space is constrained.
const PRIOR_VISIT_SHORT_LABELS = {
  never: "Never",
  over_year: "Over 1 yr ago",
  within_year: "Within 1 yr",
  within_3_months: "Within 3 mo",
  within_month: "Last month",
  within_week: "Last week",
};

const NEW_LAPSED_KEYS = ["never", "over_year", "within_year"];
const RECENT_REGULAR_KEYS = ["within_3_months", "within_month", "within_week"];

// Ordered so the "positive outcome" end (very_likely) sits on the right. Likely
// buckets use green (darkest on very_likely); unlikely buckets use grey
// (darkest on very_unlikely), mirroring the Coupon-reach convention.
const RETURN_LIKELIHOOD_BAR_ORDER = [
  "very_unlikely",
  "somewhat_unlikely",
  "somewhat_likely",
  "very_likely",
];

const RETURN_LIKELIHOOD_LABELS = {
  very_likely: "Very likely",
  somewhat_likely: "Somewhat likely",
  somewhat_unlikely: "Somewhat unlikely",
  very_unlikely: "Very unlikely",
};

const RETURN_LIKELIHOOD_COLORS = {
  very_unlikely: "gray.700",
  somewhat_unlikely: "gray.300",
  somewhat_likely: "green.300",
  very_likely: "green.700",
};

const LIKELY_KEYS = ["very_likely", "somewhat_likely"];
const UNLIKELY_KEYS = ["very_unlikely", "somewhat_unlikely"];

const sumKeys = (counts, keys) =>
  keys.reduce((acc, k) => acc + (counts?.[k] || 0), 0);

// Small bilingual (EN+FR) stop-word list. Not exhaustive — just enough to
// keep the most common filler words out of the word cloud.
const STOP_WORDS = new Set([
  "the", "and", "a", "an", "is", "was", "were", "of", "to", "in", "for", "on",
  "with", "at", "by", "from", "as", "that", "this", "it", "its", "be", "been",
  "are", "or", "but", "not", "no", "if", "then", "than", "so", "such", "my",
  "your", "their", "his", "her", "we", "you", "they", "them", "us", "had",
  "have", "has", "do", "does", "did", "will", "would", "could", "should",
  "may", "might", "can", "all", "very", "really", "just", "only", "also",
  "too", "more", "most", "some", "any", "every", "each", "much", "many",
  "when", "what", "who", "how", "why", "where", "there", "here", "into",
  "out", "about", "over", "again",
  // French
  "le", "la", "les", "un", "une", "des", "du", "de", "et", "ou", "mais",
  "donc", "pour", "par", "avec", "sans", "dans", "sur", "sous", "vers",
  "chez", "que", "qui", "quoi", "ce", "cette", "ces", "il", "elle", "ils",
  "elles", "je", "tu", "nous", "vous", "ne", "pas", "plus", "moins", "tres",
  "trop", "aussi", "alors", "encore", "deja", "etre", "est", "sont", "etait",
  "avoir", "fait", "faire", "ont", "leur", "leurs", "son", "ses", "mon",
  "mes", "ton", "tes", "votre", "vos", "notre", "nos",
]);

const buildWordFrequencies = (texts, maxWords = 40) => {
  const counts = new Map();
  for (const text of texts) {
    if (!text) continue;
    // Lowercase + replace non-letter chars with spaces. \p{L} matches any
    // Unicode letter so accented characters survive.
    const normalized = text.toLowerCase().replace(/[^\p{L}\s'-]/gu, " ");
    for (const raw of normalized.split(/\s+/)) {
      const word = raw.replace(/^['-]+|['-]+$/g, "");
      if (word.length < 3) continue;
      if (STOP_WORDS.has(word)) continue;
      counts.set(word, (counts.get(word) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, maxWords);
};

const CouponReachCard = ({ surveys }) => {
  const counts = surveys?.priorVisitCounts || {};
  const total = PRIOR_VISIT_BAR_ORDER.reduce((sum, k) => sum + (counts[k] || 0), 0);
  const newLapsedCount = sumKeys(counts, NEW_LAPSED_KEYS);
  const recentRegularCount = sumKeys(counts, RECENT_REGULAR_KEYS);
  const newLapsedPct = total ? newLapsedCount / total : 0;
  const recentRegularPct = total ? recentRegularCount / total : 0;

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Heading size="sm" color="gray.700" mb={3}>
        Coupon reach
      </Heading>

      {total === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No survey responses yet.
        </Text>
      ) : (
        <>
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold" mb={4}>
            {Math.round(newLapsedPct * 100)}% of users were new or lapsed
            customers
          </Text>

          <Flex
            height="28px"
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.200"
            mb={2}
          >
            {PRIOR_VISIT_BAR_ORDER.map((key) => {
              const n = counts[key] || 0;
              if (!n) return null;
              const pct = (n / total) * 100;
              return (
                <Box
                  key={key}
                  bg={PRIOR_VISIT_COLORS[key]}
                  width={`${pct}%`}
                  title={`${PRIOR_VISIT_LABELS[key]}: ${n} (${Math.round(pct)}%)`}
                />
              );
            })}
          </Flex>

          <Flex justify="space-between" fontSize="xs" color="gray.600" mb={4}>
            <Text>
              New &amp; lapsed customers: {Math.round(newLapsedPct * 100)}% (
              {newLapsedCount})
            </Text>
            <Text>
              Recent regulars: {Math.round(recentRegularPct * 100)}% (
              {recentRegularCount})
            </Text>
          </Flex>

          <SimpleGrid
            columns={{ base: 1, sm: 2 }}
            spacingY={1.5}
            spacingX={4}
            gridAutoFlow={{ base: "row", sm: "column" }}
            gridTemplateRows={{ base: "auto", sm: "repeat(3, auto)" }}
          >
            {PRIOR_VISIT_BAR_ORDER.map((key) => {
              const n = counts[key] || 0;
              const pct = total ? Math.round((n / total) * 100) : 0;
              return (
                <HStack key={key} spacing={2} fontSize="sm">
                  <Box
                    width="10px"
                    height="10px"
                    borderRadius="sm"
                    bg={PRIOR_VISIT_COLORS[key]}
                    flexShrink={0}
                  />
                  <Text color="gray.700" flex="1" noOfLines={1}>
                    {PRIOR_VISIT_LABELS[key]}
                  </Text>
                  <Text color="gray.600">{pct}%</Text>
                </HStack>
              );
            })}
          </SimpleGrid>
        </>
      )}
    </Box>
  );
};

const ReturnIntentCard = ({ surveys }) => {
  const counts = surveys?.returnLikelihoodCounts || {};
  const total = RETURN_LIKELIHOOD_BAR_ORDER.reduce(
    (sum, k) => sum + (counts[k] || 0),
    0
  );
  const likelyCount = sumKeys(counts, LIKELY_KEYS);
  const unlikelyCount = sumKeys(counts, UNLIKELY_KEYS);
  const likelyPct = total ? likelyCount / total : 0;
  const unlikelyPct = total ? unlikelyCount / total : 0;

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Heading size="sm" color="gray.700" mb={3}>
        Return intent overall
      </Heading>

      {total === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No survey responses yet.
        </Text>
      ) : (
        <>
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold" mb={4}>
            {Math.round(likelyPct * 100)}% say they are likely to return
          </Text>

          <Flex
            height="28px"
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.200"
            mb={2}
          >
            {RETURN_LIKELIHOOD_BAR_ORDER.map((key) => {
              const n = counts[key] || 0;
              if (!n) return null;
              const pct = (n / total) * 100;
              return (
                <Box
                  key={key}
                  bg={RETURN_LIKELIHOOD_COLORS[key]}
                  width={`${pct}%`}
                  title={`${RETURN_LIKELIHOOD_LABELS[key]}: ${n} (${Math.round(
                    pct
                  )}%)`}
                />
              );
            })}
          </Flex>

          <Flex justify="space-between" fontSize="xs" color="gray.600" mb={4}>
            <Text>
              Unlikely to return: {Math.round(unlikelyPct * 100)}% (
              {unlikelyCount})
            </Text>
            <Text>
              Likely to return: {Math.round(likelyPct * 100)}% ({likelyCount})
            </Text>
          </Flex>

          <SimpleGrid
            columns={{ base: 1, sm: 2 }}
            spacingY={1.5}
            spacingX={4}
            gridAutoFlow={{ base: "row", sm: "column" }}
            gridTemplateRows={{ base: "auto", sm: "repeat(2, auto)" }}
          >
            {RETURN_LIKELIHOOD_BAR_ORDER.map((key) => {
              const n = counts[key] || 0;
              const pct = total ? Math.round((n / total) * 100) : 0;
              return (
                <HStack key={key} spacing={2} fontSize="sm">
                  <Box
                    width="10px"
                    height="10px"
                    borderRadius="sm"
                    bg={RETURN_LIKELIHOOD_COLORS[key]}
                    flexShrink={0}
                  />
                  <Text color="gray.700" flex="1" noOfLines={1}>
                    {RETURN_LIKELIHOOD_LABELS[key]}
                  </Text>
                  <Text color="gray.600">{pct}%</Text>
                </HStack>
              );
            })}
          </SimpleGrid>
        </>
      )}
    </Box>
  );
};

// Custom dot for the return-intent-by-recency chart: a filled circle with the
// percentage rendered inside it. Blue for new/lapsed segments, gray for
// recent regulars, so the eye can tell at a glance whether the coupon
// converted one-timers into repeat customers.
const ReturnIntentDot = (props) => {
  const { cx, cy, payload } = props;
  if (
    payload == null ||
    payload.pct == null ||
    !Number.isFinite(payload.pct) ||
    cx == null ||
    cy == null
  ) {
    return null;
  }
  const fill = payload.isNewLapsed ? "#38A169" : "#718096"; // green.600 / gray.500
  return (
    <g>
      <circle cx={cx} cy={cy} r={18} fill={fill} stroke="white" strokeWidth={1} />
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dy=".35em"
        fontSize="11"
        fontWeight="600"
        fill="white"
      >
        {`${Math.round(payload.pct * 100)}%`}
      </text>
    </g>
  );
};

const ReturnIntentByRecencyCard = ({ surveys }) => {
  const byPV = surveys?.returnIntentByPriorVisit || {};

  // Build chart rows in display order. pct === null where a bucket has no
  // responses — recharts will skip those dots; `connectNulls` draws the line
  // across the gap.
  const data = PRIOR_VISIT_BAR_ORDER.map((key) => {
    const { total = 0, likely = 0 } = byPV[key] || {};
    return {
      key,
      label: PRIOR_VISIT_SHORT_LABELS[key],
      pct: total ? likely / total : null,
      total,
      likely,
      isNewLapsed: NEW_LAPSED_KEYS.includes(key),
    };
  });

  const populated = data.filter((d) => d.pct !== null);

  // Weighted averages (by response count) for the dynamic insight callout.
  const sumLikely = (rows) => rows.reduce((s, d) => s + d.likely, 0);
  const sumTotal = (rows) => rows.reduce((s, d) => s + d.total, 0);
  const newLapsedRows = populated.filter((d) => d.isNewLapsed);
  const recentRows = populated.filter((d) => !d.isNewLapsed);
  const newLapsedAvg = sumTotal(newLapsedRows)
    ? sumLikely(newLapsedRows) / sumTotal(newLapsedRows)
    : null;
  const recentAvg = sumTotal(recentRows)
    ? sumLikely(recentRows) / sumTotal(recentRows)
    : null;

  let insight = null;
  if (newLapsedAvg !== null && recentAvg !== null) {
    insight =
      newLapsedAvg >= recentAvg
        ? "New and lapsed customers show the highest return intent, meaning the coupon is converting one-time visits into repeat customers — not just rewarding people who already come regularly."
        : "Recent regulars show stronger return intent than new and lapsed visitors — the coupon is driving redemptions but additional follow-up may help convert new visits into repeat customers.";
  }

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Heading size="sm" color="gray.700" mb={3}>
        Return intent by prior visit recency
      </Heading>

      {populated.length === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No survey responses yet.
        </Text>
      ) : (
        <>
          <Text fontSize="sm" color="gray.600" mb={3}>
            % of respondents who said &ldquo;Very likely&rdquo; or
            &ldquo;Somewhat likely&rdquo; to return
          </Text>

          <HStack spacing={4} mb={3} flexWrap="wrap">
            <HStack spacing={2}>
              <Box width="10px" height="10px" borderRadius="full" bg="#38A169" />
              <Text fontSize="xs" color="gray.700">
                New &amp; lapsed
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box width="10px" height="10px" borderRadius="full" bg="#718096" />
              <Text fontSize="xs" color="gray.700">
                Recent regulars
              </Text>
            </HStack>
          </HStack>

          <Box width="100%" height={{ base: "240px", md: "280px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 24, right: 24, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#4A5568" }}
                  interval={0}
                />
                <YAxis
                  domain={[
                    (dataMin) => {
                      if (!Number.isFinite(dataMin)) return 0;
                      return Math.max(
                        0,
                        Math.floor((dataMin - 0.1) * 10) / 10
                      );
                    },
                    1,
                  ]}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                  tick={{ fontSize: 11, fill: "#4A5568" }}
                />
                <Tooltip
                  formatter={(v) =>
                    v == null
                      ? ["—", "Return intent"]
                      : [`${Math.round(v * 100)}%`, "Return intent"]
                  }
                />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke="#90CDF4"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={<ReturnIntentDot />}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {insight && (
            <Box
              mt={4}
              bg="gray.800"
              color="gray.100"
              p={3}
              borderRadius="md"
              fontSize="sm"
            >
              <Text>
                <Text as="span" fontWeight="bold">
                  Key insight
                </Text>{" "}
                — {insight}
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const ServiceRatingDistributionCard = ({ surveys }) => {
  const counts = surveys?.serviceRatingCounts || {};
  const total = [1, 2, 3, 4, 5].reduce((s, k) => s + (counts[k] || 0), 0);
  const avg = surveys?.avgServiceRating;

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Heading size="sm" color="gray.700" mb={3}>
        Service rating distribution
      </Heading>

      {total === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No survey responses yet.
        </Text>
      ) : (
        <>
          <Flex align="baseline" gap={3} mb={1} flexWrap="wrap">
            <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
              {avg != null ? Number(avg).toFixed(1) : "—"}
            </Text>
            <Text fontSize="2xl" color="yellow.500" lineHeight="1">
              ★
            </Text>
            <Text fontSize="sm" color="gray.600">
              Average rating
            </Text>
          </Flex>
          <Text fontSize="xs" color="gray.500" mb={4}>
            Based on {total} {total === 1 ? "response" : "responses"}
          </Text>

          <VStack spacing={2} align="stretch">
            {[5, 4, 3, 2, 1].map((rating) => {
              const n = counts[rating] || 0;
              const pct = total ? (n / total) * 100 : 0;
              return (
                <HStack key={rating} spacing={3} fontSize="sm">
                  <HStack spacing={1} minW="40px" justify="flex-end">
                    <Text color="gray.700">{rating}</Text>
                    <Text color="yellow.500" lineHeight="1">
                      ★
                    </Text>
                  </HStack>
                  <Box
                    flex="1"
                    bg="gray.200"
                    height="10px"
                    borderRadius="full"
                    overflow="hidden"
                  >
                    <Box
                      bg="green.500"
                      height="100%"
                      width={`${pct}%`}
                      borderRadius="full"
                    />
                  </Box>
                  <Text color="gray.600" minW="70px" textAlign="right">
                    {n} ({Math.round(pct)}%)
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </>
      )}
    </Box>
  );
};

const ServiceVsReturnIntentCard = ({ surveys }) => {
  const matrix = surveys?.returnLikelihoodByServiceRating || {};
  const totals = [1, 2, 3, 4, 5].reduce((acc, rating) => {
    acc[rating] = RETURN_LIKELIHOOD_BAR_ORDER.reduce(
      (s, k) => s + (matrix[rating]?.[k] || 0),
      0
    );
    return acc;
  }, {});
  const grandTotal = Object.values(totals).reduce((s, n) => s + n, 0);

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Heading size="sm" color="gray.700" mb={1}>
        Service rating × return intent
      </Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Distribution of return likelihood at each star rating
      </Text>

      {grandTotal === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No survey responses yet.
        </Text>
      ) : (
        <>
          <VStack spacing={2} align="stretch" mb={4}>
            {[5, 4, 3, 2, 1].map((rating) => {
              const ratingTotal = totals[rating];
              return (
                <HStack key={rating} spacing={3} fontSize="sm">
                  <HStack spacing={1} minW="40px" justify="flex-end">
                    <Text color="gray.700">{rating}</Text>
                    <Text color="yellow.500" lineHeight="1">
                      ★
                    </Text>
                  </HStack>
                  <Flex
                    flex="1"
                    height="18px"
                    borderRadius="md"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.200"
                    bg="white"
                  >
                    {ratingTotal > 0 &&
                      RETURN_LIKELIHOOD_BAR_ORDER.map((key) => {
                        const n = matrix[rating]?.[key] || 0;
                        if (!n) return null;
                        const pct = (n / ratingTotal) * 100;
                        return (
                          <Box
                            key={key}
                            bg={RETURN_LIKELIHOOD_COLORS[key]}
                            width={`${pct}%`}
                            title={`${rating}★ → ${RETURN_LIKELIHOOD_LABELS[key]}: ${n} (${Math.round(
                              pct
                            )}%)`}
                          />
                        );
                      })}
                  </Flex>
                </HStack>
              );
            })}
          </VStack>

          <HStack spacing={3} align="start">
            <Box minW="40px" />
            <SimpleGrid
              flex="1"
              columns={{ base: 1, sm: 2 }}
              spacingY={1.5}
              spacingX={4}
              gridAutoFlow={{ base: "row", sm: "column" }}
              gridTemplateRows={{ base: "auto", sm: "repeat(2, auto)" }}
            >
              {RETURN_LIKELIHOOD_BAR_ORDER.map((key) => (
                <HStack key={key} spacing={2} fontSize="sm">
                  <Box
                    width="10px"
                    height="10px"
                    borderRadius="sm"
                    bg={RETURN_LIKELIHOOD_COLORS[key]}
                    flexShrink={0}
                  />
                  <Text color="gray.700" flex="1" noOfLines={1}>
                    {RETURN_LIKELIHOOD_LABELS[key]}
                  </Text>
                </HStack>
              ))}
            </SimpleGrid>
          </HStack>
        </>
      )}
    </Box>
  );
};

const FeedbackCard = ({ surveys, businessName, businessId }) => {
  const entries = surveys?.feedbackEntries || [];
  const hasFeedback = entries.length > 0;

  const words = hasFeedback
    ? buildWordFrequencies(entries.map((e) => e.businessFeedback))
    : [];

  // Map frequency → font size with a log scale so a single very-frequent word
  // doesn't crush the rest of the cloud.
  const fontFor = (count, max, min) => {
    if (max === min) return 18;
    const t = (Math.log(count) - Math.log(min)) / (Math.log(max) - Math.log(min));
    return Math.round(12 + t * 18); // 12px → 30px
  };

  const handleDownload = () => {
    if (!hasFeedback) return;

    const groups = { 5: [], 4: [], 3: [], 2: [], 1: [], unrated: [] };
    for (const e of entries) {
      const key =
        e.serviceRating >= 1 && e.serviceRating <= 5
          ? e.serviceRating
          : "unrated";
      groups[key].push(e);
    }

    const lines = [];
    const safeName = businessName || "this business";
    lines.push(`Customer feedback — ${safeName}`);
    lines.push(`Downloaded: ${new Date().toLocaleString()}`);
    lines.push(`Total responses with feedback: ${entries.length}`);
    lines.push("");

    const sep =
      "================================================================";
    const order = [1, 2, 3, 4, 5, "unrated"];
    for (const key of order) {
      const items = groups[key];
      if (!items.length) continue;
      const heading =
        key === "unrated"
          ? "NO RATING"
          : `${key} STAR${key === 1 ? "" : "S"}`;
      lines.push(sep);
      lines.push(`${heading}  (n = ${items.length})`);
      lines.push(sep);
      lines.push("");
      for (const e of items) {
        const date = e.submittedAt ? formatCsvDate(e.submittedAt) : "";
        // Preserve in-text line breaks; just strip surrounding whitespace.
        lines.push(`[${date}]`);
        lines.push(e.businessFeedback.trim());
        lines.push("");
      }
    }

    const content = lines.join("\r\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `feedback-${businessId}-${formatCsvDate(
      new Date().toISOString()
    )}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor="gray.200"
    >
      <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
        <Box>
          <Heading size="sm" color="gray.700">
            Feedback
          </Heading>
          <Text fontSize="sm" color="gray.600">
            {hasFeedback
              ? `${entries.length} ${
                  entries.length === 1 ? "response" : "responses"
                } with comments`
              : "Open-ended customer comments"}
          </Text>
        </Box>
        {hasFeedback && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FaDownload />}
            onClick={handleDownload}
          >
            Download
          </Button>
        )}
      </Flex>

      {!hasFeedback ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          No written feedback submitted yet.
        </Text>
      ) : words.length === 0 ? (
        <Text color="gray.500" fontSize="sm" py={2}>
          Feedback received, but no common words to display yet.
        </Text>
      ) : (
        <Box
          bg="white"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          p={4}
          minH="180px"
        >
          {(() => {
            const max = words[0][1];
            const min = words[words.length - 1][1];
            return (
              <Flex wrap="wrap" gap={3} justify="center" align="baseline">
                {words.map(([word, count]) => {
                  const size = fontFor(count, max, min);
                  const ratio = max === min ? 1 : (count - min) / (max - min);
                  const color =
                    ratio > 0.66
                      ? "green.700"
                      : ratio > 0.33
                        ? "green.500"
                        : "green.400";
                  return (
                    <Text
                      key={word}
                      fontSize={`${size}px`}
                      fontWeight={ratio > 0.5 ? "bold" : "medium"}
                      color={color}
                      lineHeight="1.1"
                      title={`${word} — ${count} ${
                        count === 1 ? "mention" : "mentions"
                      }`}
                    >
                      {word}
                    </Text>
                  );
                })}
              </Flex>
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

const BusinessDashboard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = UserAuth();

  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [redemptions, setRedemptions] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.uid || !businessId) return;
      setLoading(true);
      try {
        const businessDoc = await getDoc(doc(db, "businesses", businessId));
        if (!businessDoc.exists()) {
          toast({
            title: "Not found",
            description: "This coupon could not be loaded.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setLoading(false);
          return;
        }
        setBusiness(businessDoc.data());

        const idToken = sessionStorage.getItem("idToken");
        const authHeader = { Authorization: `Bearer ${idToken}` };
        const [redemptionsRes, surveysRes] = await Promise.all([
          axios.get(`${apiUrl}/business/${businessId}/redemptions`, {
            headers: authHeader,
          }),
          axios.get(`${apiUrl}/business/${businessId}/surveys`, {
            headers: authHeader,
          }),
        ]);
        setRedemptions(redemptionsRes.data);
        setSurveys(surveysRes.data);
      } catch (err) {
        console.error("Failed to load business dashboard", err);
        toast({
          title: "Error",
          description: "Could not load dashboard data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, businessId, toast]);

  if (loading) {
    return (
      <>
        <Navbar />
        <Flex minH="80vh" justify="center" align="center">
          <Spinner size="xl" />
        </Flex>
      </>
    );
  }

  if (!business) {
    return (
      <>
        <Navbar />
        <Flex minH="80vh" justify="center" align="center">
          <Text>Coupon unavailable.</Text>
        </Flex>
      </>
    );
  }

  const approved = business.approved === true;
  const businessName = localizedField(business.name);
  const offerTitle = localizedField(business.offerTitle);
  const validationUrl = business?.passcode
    ? `${window.location.origin}/validate/${business.passcode}`
    : "";

  return (
    <>
      <Navbar />
      <Box
        minH="calc(100vh - 60px)"
        bg="primaryBackground"
        pt={{ base: "40px", md: "30px" }}
        px={{ base: 4, sm: 6, md: 10 }}
      >
        <VStack
          spacing={{ base: 6, md: 8 }}
          maxWidth="1200px"
          margin="0 auto"
          py={{ base: 6, md: 8 }}
          width="100%"
          align="stretch"
        >
          <Flex
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <Box>
              <Heading as="h1" size="lg" color="heading">
                {businessName || "Your coupon"}
              </Heading>
              {offerTitle && (
                <Text color="gray.600" mt={1}>
                  {offerTitle}
                </Text>
              )}
            </Box>
            <HStack spacing={3} flexWrap="wrap">
              {business.campaignId && (
                <ChakraTooltip
                  label="View campaign page"
                  hasArrow
                  placement="top"
                >
                  <IconButton
                    as="a"
                    href={`/support/${business.campaignId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaDollarSign />}
                    aria-label="View campaign page"
                    size="sm"
                    variant="outline"
                  />
                </ChakraTooltip>
              )}
              <ChakraTooltip
                label="Print instructions"
                hasArrow
                placement="top"
              >
                <IconButton
                  as="a"
                  href={`/business-instructions/${businessId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<FaInfoCircle />}
                  aria-label="Print instructions"
                  size="sm"
                  variant="outline"
                />
              </ChakraTooltip>
              {validationUrl && (
                <>
                  <ChakraTooltip
                    label="Show redemption page QR code"
                    hasArrow
                    placement="top"
                  >
                    <IconButton
                      icon={<FaQrcode />}
                      aria-label="Show redemption page QR code"
                      onClick={() => setIsQRModalOpen(true)}
                      size="sm"
                      variant="outline"
                    />
                  </ChakraTooltip>
                  <ChakraTooltip
                    label="Open redemption page"
                    hasArrow
                    placement="top"
                  >
                    <IconButton
                      as="a"
                      href={validationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<FaExternalLinkAlt />}
                      aria-label="Open redemption page"
                      size="sm"
                      variant="outline"
                    />
                  </ChakraTooltip>
                </>
              )}
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(`/edit-business?businessId=${businessId}`)
                }
              >
                Edit Coupon
              </Button>
            </HStack>
          </Flex>

          <Alert
            status={approved ? "success" : "warning"}
            borderRadius="md"
            variant="left-accent"
          >
            <AlertIcon />
            <Box>
              <AlertTitle>
                {approved ? "Coupon active" : "Pending approval"}
              </AlertTitle>
              <AlertDescription>
                {approved
                  ? "Your coupon is live and customers can redeem it."
                  : "Your campaign organizer is reviewing your submission. You'll be able to track redemptions here once it's approved."}
              </AlertDescription>
            </Box>
          </Alert>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
              <Flex justify="space-between" align="flex-start">
                <StatLabel fontSize="lg">Total redemptions</StatLabel>
                <Icon as={FaTicketAlt} boxSize={7} color="black" />
              </Flex>
              <StatNumber fontSize="3xl">
                {redemptions?.totalCount ?? 0}
              </StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="md">
              <Flex justify="space-between" align="flex-start">
                <StatLabel fontSize="lg">Last 30 days</StatLabel>
                <Icon as={FaCalendarCheck} boxSize={7} color="black" />
              </Flex>
              <StatNumber fontSize="3xl">
                {redemptions?.last30Days?.reduce(
                  (sum, d) => sum + d.count,
                  0
                ) ?? 0}
              </StatNumber>
            </Stat>
          </SimpleGrid>

          <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="lg"
            boxShadow="md"
          >
            <Heading size="md" mb={5}>
              Redemptions
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
              <Box
                bg="gray.50"
                borderRadius="lg"
                p={{ base: 4, md: 5 }}
                border="1px solid"
                borderColor="gray.200"
              >
                <Heading size="sm" color="gray.700" mb={3}>
                  Redemptions — last 30 days
                </Heading>
                <Box width="100%" height={{ base: "240px", md: "300px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={redemptions?.last30Days || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatChartDay}
                        interval="preserveStartEnd"
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(label) => label}
                        formatter={(value) => [value, "Redemptions"]}
                      />
                      <Bar
                        dataKey="count"
                        fill="#38A169"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              <Box
                bg="gray.50"
                borderRadius="lg"
                p={{ base: 4, md: 5 }}
                border="1px solid"
                borderColor="gray.200"
              >
                <Flex
                  justify="space-between"
                  align="center"
                  mb={3}
                  gap={3}
                  flexWrap="wrap"
                >
                  <Heading size="sm" color="gray.700">
                    Recent redemptions
                  </Heading>
                  {redemptions?.recent?.length ? (
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FaDownload />}
                      onClick={() => {
                        const header = ["Date", "Time", "Notes"];
                        const rows = redemptions.recent.map((r) => [
                          formatCsvDate(r.date),
                          formatCsvTime(r.date),
                          r.notes || "",
                        ]);
                        const csv = [header, ...rows]
                          .map((row) => row.map(csvEscape).join(","))
                          .join("\r\n");
                        const blob = new Blob([csv], {
                          type: "text/csv;charset=utf-8;",
                        });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `redemptions-${businessId}-${formatCsvDate(
                          new Date().toISOString()
                        )}.csv`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download
                    </Button>
                  ) : null}
                </Flex>
                {redemptions?.recent?.length ? (
                  <Box overflowX="auto">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>When</Th>
                          <Th>Notes</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {redemptions.recent.map((r, i) => (
                          <Tr key={i}>
                            <Td>{formatDateTime(r.date)}</Td>
                            <Td>{r.notes || ""}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Text color="gray.500">No redemptions yet.</Text>
                )}
              </Box>
            </SimpleGrid>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="lg"
            boxShadow="md"
          >
            <Heading size="md" mb={4}>
              Customer feedback
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 5 }}>
              <Stat display="flex" flexDirection="column" height="100%">
                <StatLabel>Responses</StatLabel>
                <StatNumber fontSize="2xl" mt="auto">
                  {surveys?.count ?? 0}
                </StatNumber>
              </Stat>
              <Stat display="flex" flexDirection="column" height="100%">
                <StatLabel>Avg service rating</StatLabel>
                <StatNumber fontSize="2xl" mt="auto">
                  {formatStarRating(surveys?.avgServiceRating)}
                </StatNumber>
              </Stat>
              <Stat display="flex" flexDirection="column" height="100%">
                <StatLabel>Likely to return</StatLabel>
                <StatNumber fontSize="2xl" mt="auto">
                  {formatPct(surveys?.likelyToReturnPct)}
                </StatNumber>
              </Stat>
              <Stat display="flex" flexDirection="column" height="100%">
                <StatLabel>New / lapsed visitors</StatLabel>
                <StatNumber fontSize="2xl" mt="auto">
                  {formatPct(surveys?.newLapsedPct)}
                </StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="lg"
            boxShadow="md"
          >
            <Heading size="md" mb={5}>
              Survey Analytics
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
              <CouponReachCard surveys={surveys} />
              <ReturnIntentCard surveys={surveys} />
              <ServiceRatingDistributionCard surveys={surveys} />
              <ServiceVsReturnIntentCard surveys={surveys} />
              <ReturnIntentByRecencyCard surveys={surveys} />
              <FeedbackCard
                surveys={surveys}
                businessName={businessName}
                businessId={businessId}
              />
            </SimpleGrid>
          </Box>

        </VStack>
      </Box>

      <Modal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Redemption QR Code
            {businessName ? ` — ${businessName}` : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} py={2}>
              {validationUrl ? (
                <>
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <QRCode value={validationUrl} size={256} level="H" />
                  </Box>
                  <ChakraTooltip
                    label={validationUrl}
                    hasArrow
                    placement="top"
                    openDelay={200}
                  >
                    <IconButton
                      icon={<FaCopy />}
                      aria-label="Copy redemption URL"
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(validationUrl);
                          toast({
                            title: "URL copied to clipboard",
                            status: "success",
                            duration: 2000,
                            isClosable: true,
                          });
                        } catch (err) {
                          toast({
                            title: "Failed to copy URL",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                          });
                        }
                      }}
                    />
                  </ChakraTooltip>
                </>
              ) : (
                <Text color="gray.600">
                  No redemption code on file for this business.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsQRModalOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BusinessDashboard;
